import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { getSubmissions, updateMarks } from "src/Services/instructor_api";
import "../Common/css/submissions.css";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { Box, Button, TextField } from "@mui/material";

export default function Submissions() {
    const token = sessionStorage.getItem("token") || "";
    const location = useLocation();
    const data = location.state;

    const [searchTerm, setSearchTerm] = useState("");
    const [submissions, setSubmissions] = useState<any[]>([]);
    const [savingStatus, setSavingStatus] = useState<{ [key: number]: "idle" | "saving" | "saved" }>({});

    useEffect(() => {
        handleGetSubmissions();
        //console.log(filteredRows);

    }, [token]);

    const handleGetSubmissions = async () => {
        try {
            const response = await getSubmissions(
                data.programme,
                data.stream,
                data.subject,
                data.materialAssignmentId,
                token
            );
            setSubmissions(response.data);
        } catch (error) {
            console.error(error);
        }
    };

    const base64ToBlob = (base64: string, mimeType: string = "application/pdf") => {
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length).fill(0).map((_, i) =>
            byteCharacters.charCodeAt(i)
        );
        const byteArray = new Uint8Array(byteNumbers);
        return new Blob([byteArray], { type: mimeType });
    };

    const handleDownloadPdf = (fileBase64: string, fullName: string, originalFileName: string) => {
        const blob = base64ToBlob(fileBase64);

        const safeName = fullName.replace(/\s+/g, "_");
        const baseFileName = originalFileName.replace(".pdf", "");
        const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, ""); // e.g., 20250628
        const fileName = `${safeName}_${baseFileName}_${dateStr}.pdf`;

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleUpdateMarks = async (row: any) => {

        const body = {
            userId: row.userId,
            assignmentId: row.id.split("_")[1],
            marks: row.marks,
        };

        setSubmissions((prev) =>
            prev.map((r) => (r.userId === row.userId ? { ...r, marks: row.marks } : r))
        );

        setSavingStatus((prev) => ({ ...prev, [row.id]: "saving" }));

        try {
            const res = await updateMarks(body, token);
            if (res.status === 200) {
                setSavingStatus((prev) => ({ ...prev, [row.id]: "saved" }));
                setTimeout(() => {
                    setSavingStatus((prev) => ({ ...prev, [row.id]: "idle" }));
                }, 2000);
            }
        } catch (error) {
            console.error("Error updating marks:", error);
            setSavingStatus((prev) => ({ ...prev, [row.id]: "idle" }));
        }
    };

    // DataGrid columns
    const columns: GridColDef[] = [
        { field: "fullName", headerName: "Full Name", flex: 1 },
        { field: "latestAttempt", headerName: "Attempts", flex: 1 },
        {
            field: "markStatus",
            headerName: "Status",
            flex: 1,
            valueFormatter: ({ value }) => (value === 1 ? "Not marked" : value === null ? "Not Submitted" : "Marked"),
        },
        {
            field: "marks",
            headerName: "Marks",
            flex: 1.5,
            renderCell: (params) => {
                const status = savingStatus[params.row.id];
                const isDisabled = params.row.markStatus === null;

                return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <TextField
                            type="number"
                            variant="outlined"
                            size="small"
                            value={params.row.marks ?? ""}
                            onFocus={(e) => e.target.select()}
                            onChange={(e) => {
                                const newValue = parseInt(e.target.value);
                                if (!isNaN(newValue) && newValue <= 100) {
                                    const updatedRow = { ...params.row, marks: newValue };
                                    handleUpdateMarks(updatedRow);
                                }
                            }}
                            disabled={isDisabled}
                            inputProps={{
                                max: 100,
                                style: { appearance: "textfield" },
                                onWheel: (e) => e.currentTarget.blur(),
                            }}
                            sx={{
                                "& input": {
                                    color: isDisabled ? "black !important" : "white",
                                },
                                backgroundColor: isDisabled ? "grey" : "black",
                                borderRadius: 1,
                                textAlign: "right !important",
                                "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "white",
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "white",
                                },
                                "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                                    WebkitAppearance: "none",
                                    margin: 0,
                                },
                                "& input[type=number]": {
                                    MozAppearance: "textfield",
                                },
                                width: 150,
                                "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                        borderColor: "white",
                                    },
                                    "&:hover fieldset": {
                                        borderColor: "white",
                                    },
                                    "&.Mui-focused fieldset": {
                                        borderColor: "white",
                                    },
                                },
                            }}
                        />
                        {!isDisabled && status === "saving" && (
                            <span style={{ color: "#ffcc00", fontSize: "0.8rem" }}>Saving...</span>
                        )}
                        {!isDisabled && status === "saved" && (
                            <span style={{ color: "#00FF99", fontSize: "0.8rem" }}>Saved ✅</span>
                        )}
                    </Box>
                );
            },
        },
        {
            field: "download",
            headerName: "Download PDF",
            flex: 1,
            renderCell: (params) => {
                const isDisabled = params.row.markStatus === null;

                return (
                    <Button
                        variant="contained"
                        size="small"
                        className="download-button"
                        disabled={isDisabled}
                        sx={{
                            backgroundColor: isDisabled ? "grey !important" : "#FFEC00",
                            color: "black",
                            "&:hover": {
                                backgroundColor: isDisabled ? "grey" : "#FFEC00",
                            },
                        }}
                        onClick={() =>
                            handleDownloadPdf(
                                params.row.latestFile,
                                params.row.fullName,
                                data.fileName
                            )
                        }
                    >
                        Download
                    </Button>
                );
            },
        }
    ];

    const filteredRows = Array.from(
        new Map(
            submissions
                .filter((sub) => {
                    const search = searchTerm.toLowerCase();
                    return (
                        (sub.fullName ?? "").toLowerCase().includes(search) ||
                        (sub.email ?? "").toLowerCase().includes(search)
                    );
                })
                .map((row, index) => [`${row.userId}`, { ...row, id: `${row.userId}_${row.id}` }])
        ).values()
    );

    return (
        <div className="comman-outer">
            <Box sx={{ p: 3 }}>
                <h1 className="submissions-title">{data.fileName.split(".pdf")[0]} Submissions</h1>

                <Box mb={2}>
                    <TextField
                        size="small"
                        className="search-input"
                        placeholder="Search submissions..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        variant="outlined"
                        sx={{
                            "& input": {
                                color: "white",
                            },
                        }}
                    />
                </Box>

                <DataGrid
                    rows={filteredRows}
                    columns={columns}
                    autoHeight
                    sx={{
                        "& .MuiDataGrid-columnHeaders": {
                            backgroundColor: "black",
                            color: "white",
                            fontWeight: "bold",
                        },
                        "& .MuiDataGrid-footerContainer": {
                            color: "white !important",
                            backgroundColor: "#1d1d1d",
                        },
                        "& .MuiTablePagination-root": {
                            color: "white",
                        },
                        "& .MuiSelect-icon": {
                            color: "white",
                        },
                        "& .MuiInputBase-root": {
                            color: "white",
                        },
                        "& .Mui-disabled": {
                            color: "white !important",
                        },
                        "& .MuiDataGrid-cell": {
                            color: "white",
                        },
                    }}
                />
            </Box>
        </div>
    );
}
